import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Clock, ArrowLeft, Navigation, Check, Truck, ChevronDown, X, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { getApiUrl } from "../utils/api";

const getAPI = () => getApiUrl();

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchShareData = async () => {
      try {
        // Try short URL first, fall back to legacy
        const response = await axios.get(`${getAPI()}/s/${shareId}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Share link not found");
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();
  }, [shareId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownOpen && !e.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
        <div className="w-8 h-8 border-3 border-[#007aff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f5f5f7] p-8">
        <MapPin className="w-16 h-16 text-[#86868b] mb-6" />
        <h1 className="text-2xl font-semibold text-[#1d1d1f] mb-2">Link Not Found</h1>
        <p className="text-[#86868b] mb-8">{error}</p>
        <Link to="/">
          <Button className="bg-[#007aff] hover:bg-[#0066d6] rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Route-Z
          </Button>
        </Link>
      </div>
    );
  }

  // Full route share with dropdown
  if (data.type === "route") {
    const destinations = data.destinations || [];
    
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-4" data-testid="share-page">
        {/* Content */}
        <div className="w-full max-w-sm">
          {/* Hero Card */}
          <div className="bg-[#007aff] rounded-2xl p-8 text-white text-center mb-4 shadow-lg">
            <Truck className="w-14 h-14 mx-auto mb-4 opacity-90" />
            <h1 className="text-2xl font-semibold mb-2">Delivery ETA</h1>
            <p className="text-white/80 text-sm">Select your name to see your estimated arrival time</p>
          </div>

          {/* Dropdown Selector */}
          <div className="dropdown-container relative mb-4">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] flex items-center justify-between transition-all duration-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#86868b]" />
                </div>
                <span className="text-[#1d1d1f] font-medium">Find your delivery</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-[#86868b] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.16)] overflow-hidden z-20 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="max-h-80 overflow-y-auto">
                  {destinations.map((dest, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedStop({ ...dest, index: index + 1 });
                        setDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#f5f5f7] transition-colors border-b border-[rgba(0,0,0,0.06)] last:border-0"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold ${
                        dest.completed ? 'bg-[#34c759] text-white' : 'bg-[#007aff] text-white'
                      }`}>
                        {dest.completed ? <Check className="w-4 h-4" /> : index + 1}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-[#1d1d1f]">{dest.name || dest.postcode}</div>
                      </div>
                      {dest.completed && (
                        <span className="text-[10px] uppercase bg-[#34c759]/10 text-[#34c759] px-2 py-1 rounded-full font-semibold">
                          Delivered
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Helper text */}
          <p className="text-center text-sm text-[#86868b]">
            {destinations.length} {destinations.length === 1 ? 'stop' : 'stops'} on this route
          </p>
        </div>

        {/* ETA Modal */}
        {selectedStop && (
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
            onClick={() => setSelectedStop(null)}
          >
            <div 
              className="bg-white rounded-3xl w-full max-w-sm shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`p-6 text-center ${selectedStop.completed ? 'bg-[#34c759]' : 'bg-[#007aff]'} text-white relative`}>
                <button 
                  onClick={() => setSelectedStop(null)}
                  className="absolute top-4 right-4 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-2xl font-bold ${
                  selectedStop.completed ? 'bg-white/20' : 'bg-white/20'
                }`}>
                  {selectedStop.completed ? <Check className="w-8 h-8" /> : selectedStop.index}
                </div>
                <h2 className="text-xl font-semibold">{selectedStop.name || 'Delivery'}</h2>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {selectedStop.completed ? (
                  <div className="text-center">
                    <div className="text-4xl font-bold text-[#34c759] mb-2">Delivered!</div>
                    <p className="text-[#86868b]">Your package has been delivered</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-[10px] uppercase text-[#86868b] tracking-wider mb-1">Estimated Arrival</div>
                    <div className="text-5xl font-bold text-[#007aff] mb-2">{selectedStop.eta || '--:--'}</div>
                    <p className="text-[#86868b] text-sm">Your delivery is on the way</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 pb-6">
                <Button
                  onClick={() => setSelectedStop(null)}
                  className="w-full h-12 bg-[#f5f5f7] hover:bg-[#e8e8ed] text-[#1d1d1f] rounded-xl font-semibold"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer - removed */}
      </div>
    );
  }

  // Single destination share (legacy)
  return (
    <div className="min-h-screen bg-[#f5f5f7]" data-testid="share-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-[rgba(0,0,0,0.08)] px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Navigation className="w-5 h-5 text-[#007aff]" />
            <span className="font-semibold text-[#1d1d1f]">Route-Z</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-[#007aff] rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Plan Route
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto p-4">
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden">
          {/* Hero section */}
          <div className="bg-[#007aff] text-white p-8 text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h1 className="text-2xl font-semibold mb-1">Arrival Update</h1>
            <p className="text-white/80 text-sm">Someone is on their way to you</p>
          </div>

          {/* Details */}
          <div className="p-6">
            <div className="mb-6">
              <div className="text-[10px] text-[#86868b] uppercase tracking-wider mb-1">
                Destination
              </div>
              {data.destination_name && (
                <div className="text-lg font-semibold text-[#1d1d1f] mb-1" data-testid="share-name">
                  {data.destination_name}
                </div>
              )}
              <div className="text-2xl font-bold text-[#1d1d1f]" data-testid="share-postcode">
                {data.destination_postcode}
              </div>
              {data.destination_address && (
                <div className="text-sm text-[#86868b] mt-2">
                  {data.destination_address.split(',').slice(0, 3).join(', ')}
                </div>
              )}
            </div>

            <div className="border-t border-[rgba(0,0,0,0.08)] pt-6">
              <div className="text-[10px] text-[#86868b] uppercase tracking-wider mb-1">
                Estimated Arrival
              </div>
              <div className="text-4xl font-bold text-[#007aff]" data-testid="share-eta">
                {data.eta}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[rgba(0,0,0,0.08)] text-center text-xs text-[#86868b]">
              Shared via Route-Z
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
